/**
 * One funded project.
 *
 * The subsidy lists named a measure and a project and stopped there, so
 * the only way to see who received a grant, or what else was funded under
 * the same measure, was to go back and filter. Every other entity in the
 * app has a dossier; a grant is a payment of public money and deserves
 * one at least as much.
 *
 * The programme publishes considerably more than the lists ever showed —
 * a paragraph describing what each project actually does, its status, the
 * term it ran for, and the tranches paid against the contracted figure.
 * All of it was in the feed and none of it was being read.
 *
 * No outbound link to ИСУН. The identifier is the official contract
 * number and an obvious deep link exists, but 2020.eufunds.bg answers it
 * with a WAF page ("Request Rejected", 245 bytes) rather than the
 * project, and eumis2020 404s. A link that looks official and lands
 * nowhere is worse than the number itself, which a reader can search.
 * The number is therefore shown plainly and selectably.
 */

import { useQuery } from '@tanstack/react-query';
import { graphql } from '../gql';
import { request } from '../lib/graphql';
import { DataTable } from '../components/DataTable';
import {
  Amount, Breadcrumb, DetailHead, ErrorState, Fact, Link, Loading, SectionHead,
} from '../components/ui';
import { date, num, truncate } from '../lib/format';

const SUBSIDY = graphql(`
  query SubsidyDossier($id: ID!) {
    subsidies(where: { subsidy_id: $id }) {
      subsidy_id
      measure
      project_title
      amount
      year
      source
      description
      status
      total_value
      paid_amount
      start_date
      end_date
      duration_months
      places
      beneficiaries { uic name legal_form }
    }
  }
`);

/* The measure is only known once the project has been read, so its
   siblings are a second request rather than a join — the alternative is
   asking the caller for a value the page exists to discover. */
const SAME_MEASURE = graphql(`
  query SubsidiesUnderMeasure($id: ID!, $measure: String!) {
    subsidies(
      where: { measure: $measure, NOT: { subsidy_id: $id } }
      options: { limit: 25, sort: [{ amount: DESC }] }
    ) {
      subsidy_id project_title amount year
      beneficiaries { uic name }
    }
    subsidiesAggregate(where: { measure: $measure }) { count amount { sum } }
  }
`);

/** "01 юни 2015 – 31 яну 2019 · 44 месеца", from whatever parts exist. */
function term(from: string | null | undefined, to: string | null | undefined,
  months: number | null | undefined): string | null {
  const span = from && to ? `${date(from)} – ${date(to)}` : date(from ?? to);
  const length = months ? `${num(months)} мес.` : null;
  const parts = [span === '—' ? null : span, length].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

export function SubsidyPage({ id }: Readonly<{ id: string }>) {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['subsidy', id],
    queryFn: () => request(SUBSIDY, { id }),
  });
  const subsidy = data?.subsidies[0];
  const measure = subsidy?.measure ?? null;
  const siblings = useQuery({
    queryKey: ['subsidy-measure', id, measure],
    queryFn: () => request(SAME_MEASURE, { id, measure: measure! }),
    enabled: Boolean(measure),
  });

  if (isPending) return <Loading variant="cards" rows={4} />;
  if (isError) return <ErrorState message={error.message} />;
  if (!subsidy) return <ErrorState message="Проектът не е намерен в графа." />;

  const grant = subsidy.amount ?? 0;
  /* The whole project against the grant, but only where the two differ —
     equal figures mean the beneficiary co-financed nothing, and printing
     the same number twice invites the reader to look for a difference. */
  const cofinanced = subsidy.total_value != null
    && Math.round(subsidy.total_value) !== Math.round(grant);
  const period = term(subsidy.start_date, subsidy.end_date, subsidy.duration_months);

  return (
    <>
      <Breadcrumb>{[
        <Link key="up" href="#/subsidies">Субсидии</Link>,
        truncate(subsidy.project_title ?? subsidy.subsidy_id, 60),
      ]}</Breadcrumb>

      <DetailHead
        eyebrow={subsidy.measure ?? 'Проект'}
        title={subsidy.project_title ?? subsidy.subsidy_id}
        compact
        meta={[
          // The contract number as filed. It is what a reader quotes when
          // checking this against the programme's own records.
          ['Номер на договора', <span className="mono">{subsidy.subsidy_id}</span>] as const,
          subsidy.status ? ['Състояние', subsidy.status] as const : null,
          period ? ['Срок', period] as const : null,
          subsidy.places?.length
            ? ['Място на изпълнение', subsidy.places.join(', ')] as const
            : null,
          subsidy.source ? ['Източник', subsidy.source] as const : null,
          !period && subsidy.year ? ['Година', String(subsidy.year)] as const : null,
        ]}
      />

      {/* Contracted and paid sit side by side and are never divided into
          a percentage: the feed does not say whether a tranche is a share
          of the grant or of the whole project, and one in ten projects
          reports paying out more than it contracted. A ratio printed
          against the wrong denominator would read as a finding. */}
      <div className="facts">
        <Fact value={<Amount value={grant} />} label="Безвъзмездна помощ" />
        {cofinanced && (
          <Fact value={<Amount value={subsidy.total_value} />} label="Обща стойност" />
        )}
        <Fact
          value={subsidy.paid_amount != null ? <Amount value={subsidy.paid_amount} /> : '—'}
          label="Реално изплатено"
        />
        <Fact value={num(subsidy.beneficiaries.length)} label="Бенефициенти" />
      </div>

      {subsidy.description && (
        <section className="block">
          <SectionHead title="За проекта" aside="по описанието в програмата" />
          <p className="dossier-prose">{subsidy.description}</p>
        </section>
      )}

      <section className="block">
        <SectionHead title="Бенефициенти" aside="получателите по този договор" />
        <DataTable
          rows={subsidy.beneficiaries}
          rowKey={(row) => row.uic}
          empty="Няма свързан бенефициент — договорът е зареден без разпознаваем ЕИК."
          columns={[
            {
              key: 'name',
              label: 'Получател',
              render: (row) => (
                <div>
                  <Link href={`#/companies/${row.uic}`}>
                    {truncate(row.name ?? row.uic, 54)}
                  </Link>
                  <div className="uic mono">{row.uic}</div>
                </div>
              ),
            },
            {
              key: 'legal_form',
              label: 'Правна форма',
              render: (row) => row.legal_form ?? '—',
            },
          ]}
        />
      </section>

      {siblings.data && siblings.data.subsidies.length > 0 && (
        <section className="block">
          <SectionHead
            title="Други проекти по същата мярка"
            aside={`${num(siblings.data.subsidiesAggregate.count)} общо · `
              + `най-големите ${num(siblings.data.subsidies.length)}`}
          />
          <DataTable
            rows={siblings.data.subsidies}
            rowKey={(row) => row.subsidy_id}
            empty="Няма други проекти по тази мярка."
            columns={[
              {
                key: 'project',
                label: 'Проект',
                render: (row) => (
                  <div>
                    <Link href={`#/subsidies/${encodeURIComponent(row.subsidy_id)}`}>
                      {truncate(row.project_title ?? row.subsidy_id, 56)}
                    </Link>
                    <div className="uic mono">{row.subsidy_id}</div>
                  </div>
                ),
              },
              {
                key: 'beneficiary',
                label: 'Получател',
                render: (row) => {
                  const first = row.beneficiaries[0];
                  return first
                    ? (
                      <Link href={`#/companies/${first.uic}`}>
                        {truncate(first.name ?? first.uic, 30)}
                      </Link>
                    )
                    : '—';
                },
              },
              { key: 'year', label: 'Година', num: true, render: (row) => row.year ?? '—' },
              {
                key: 'amount',
                label: 'Сума',
                num: true,
                render: (row) => <Amount value={row.amount} />,
              },
            ]}
          />
        </section>
      )}
    </>
  );
}
